import React, { useCallback, useMemo } from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import TimeRangePicker from '../../components/Form/TimeRangePicker';

const clampTime = (hh, mm) => {
  const H = Math.min(23, Math.max(0, hh | 0));
  const M = Math.min(59, Math.max(0, mm | 0));
  return `${String(H).padStart(2, '0')}:${String(M).padStart(2, '0')}`;
};

const nextHour = (time = '18:00') => {
  const [h = 18, m = 0] = time.split(':').map(Number);
  return clampTime(h + 1, m);
};

const DaySchedule = ({ dayConfig, onChange }) => {
  const d = useMemo(
    () => ({
      dayOfWeek: dayConfig?.dayOfWeek ?? 0,
      isClosed: !!dayConfig?.isClosed,
      morningOpen: dayConfig?.morningOpen,
      morningClose: dayConfig?.morningClose,
      afternoonOpen: dayConfig?.afternoonOpen,
      afternoonClose: dayConfig?.afternoonClose,
      name: dayConfig?.name ?? `Day ${dayConfig?.dayOfWeek ?? 0}`,
    }),
    [dayConfig]
  );

  const hasMorning = !!(d.morningOpen && d.morningClose);
  const hasAfternoon = !!(d.afternoonOpen && d.afternoonClose);
  const hasAny = hasMorning || hasAfternoon;

  const setMorning = useCallback(
    (slot) => {
      // 若已关闭，禁止修改
      if (d.isClosed) return;

      onChange({
        ...d,
        isClosed: false,
        morningOpen: slot?.start || undefined,
        morningClose: slot?.end || undefined,
      });
    },
    [d, onChange]
  );

  const setAfternoon = useCallback(
    (slot) => {
      if (d.isClosed) return;

      onChange({
        ...d,
        isClosed: false,
        afternoonOpen: slot?.start || undefined,
        afternoonClose: slot?.end || undefined,
      });
    },
    [d, onChange]
  );

  const handleToggle = useCallback(
    (e) => {
      const enabled = e.target.checked; // checked = open

      if (!enabled) {
        // 关闭：只标记 closed，不清空时间（保留用户输入，回头再开能恢复）
        onChange({
          ...d,
          isClosed: true,
        });
        return;
      }

      // 开启：如果完全没数据，给默认上午段
      onChange({
        ...d,
        isClosed: false,
        morningOpen: hasAny ? d.morningOpen : (d.morningOpen || '09:00'),
        morningClose: hasAny ? d.morningClose : (d.morningClose || '18:00'),
        // 下午段保持原样（有就有，没有就没有）
        afternoonOpen: d.afternoonOpen,
        afternoonClose: d.afternoonClose,
      });
    },
    [d, onChange, hasAny]
  );

  const handleAddSlot = useCallback(() => {
    if (d.isClosed) return;
    if (hasAfternoon) return; // 已有第二段

    const baseEnd = d.morningClose || '18:00';
    onChange({
      ...d,
      isClosed: false,
      // 兜底确保上午段成对
      morningOpen: d.morningOpen || '09:00',
      morningClose: d.morningClose || '18:00',
      afternoonOpen: baseEnd,
      afternoonClose: nextHour(baseEnd),
    });
  }, [d, onChange, hasAfternoon]);

  const handleRemoveAfternoon = useCallback(() => {
    if (d.isClosed) return;

    onChange({
      ...d,
      afternoonOpen: undefined,
      afternoonClose: undefined,
    });
  }, [d, onChange]);

  // TimeRangePicker 若不支持 disabled：用 wrapper 禁用交互（pointerEvents）
  const pickerWrapperStyle = d.isClosed
    ? { pointerEvents: 'none', opacity: 0.6 }
    : undefined;

  return (
    <Row
      className="my-2 align-items-start flex-nowrap"
      style={{
        minHeight: 38,
        opacity: d.isClosed ? 0.75 : 1,
      }}
    >
      {/* 左侧固定宽：永远在第一行 */}
      <Col
        md={3}
        className="d-flex align-items-center gap-2 flex-shrink-0"
        style={{ minWidth: 180 }}   // 关键：固定左侧宽度，避免被挤
      >
        <Form.Check
          type="switch"
          id={`day-switch-${d.dayOfWeek}`}
          checked={!d.isClosed}
          onChange={handleToggle}
          label=""
          className="mb-0"
        />

        <span style={{ width: 48, display: 'inline-block' }}>{d.name}</span>
        <span
          style={{ width: 64, display: 'inline-block' }}
          className={d.isClosed ? 'text-muted' : ''}
        >
          {d.isClosed ? 'Closed' : 'Open'}
        </span>
      </Col>

      {/* 右侧：内部允许换行，但只在右侧区域换 */}
      <Col
        md={9}
        className="d-flex align-items-center gap-3 flex-wrap"
        style={{ minWidth: 0 }}     // 关键：允许右侧收缩，否则会把左侧挤掉
      >
        <div style={pickerWrapperStyle}>
          <TimeRangePicker
            value={{
              start: d.morningOpen || '09:00',
              end: d.morningClose || '18:00',
            }}
            onChange={setMorning}
            disabled={d.isClosed}
          />
        </div>

        {hasAfternoon ? (
          <>
            <div style={pickerWrapperStyle}>
              <TimeRangePicker
                value={{ start: d.afternoonOpen, end: d.afternoonClose }}
                onChange={setAfternoon}
                disabled={d.isClosed}
              />
            </div>

            <Button
              variant="outline-danger"
              size="sm"
              onClick={handleRemoveAfternoon}
              className="flex-shrink-0"
              disabled={d.isClosed}
              aria-label={`Remove second time slot for ${d.name}`}
            >
              ×
            </Button>
          </>
        ) : (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleAddSlot}
            className="flex-shrink-0"
            disabled={d.isClosed}
            aria-label={`Add second time slot for ${d.name}`}
          >
            +
          </Button>
        )}
      </Col>
    </Row>
  );
};

export default DaySchedule;
